import React from 'react';

const SkeletonLoader: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Header with background */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between animate-pulse">
            <div className="flex items-center space-x-4">
              <div className="w-8 h-8 bg-slate-300 rounded-full"></div>
              <div className="h-6 w-32 bg-slate-300 rounded"></div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="h-8 w-20 bg-slate-300 rounded"></div>
              <div className="h-8 w-8 bg-slate-300 rounded"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse">
          {/* Header Section Skeleton */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="h-8 w-64 bg-slate-300 rounded mb-2"></div>
              <div className="h-4 w-48 bg-slate-200 rounded"></div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="h-10 w-64 bg-slate-300 rounded"></div>
              <div className="h-10 w-48 bg-slate-300 rounded"></div>
            </div>
          </div>

          {/* Metrics Section Skeleton */}
          <div className="mb-8">
            <div className="grid grid-cols-3 gap-6">
              {/* Completion Rate Card */}
              <div className="bg-white rounded-lg p-6 border border-slate-200 shadow-sm">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="h-4 w-32 bg-slate-300 rounded"></div>
                  <div className="h-6 w-6 bg-slate-300 rounded"></div>
                </div>
                <div className="flex items-center justify-center h-32 mb-4">
                  <div className="w-24 h-24 bg-slate-300 rounded-full"></div>
                </div>
                <div className="flex items-center justify-center space-x-4">
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-slate-300 rounded-full"></div>
                    <div className="h-3 w-16 bg-slate-300 rounded"></div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-slate-300 rounded-full"></div>
                    <div className="h-3 w-20 bg-slate-300 rounded"></div>
                  </div>
                </div>
              </div>

              {/* Responses on Time Card */}
              <div className="bg-white rounded-lg p-6 border border-slate-200 shadow-sm">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="h-4 w-36 bg-slate-300 rounded"></div>
                  <div className="h-6 w-6 bg-slate-300 rounded"></div>
                </div>
                <div className="space-y-4">
                  <div className="h-8 bg-slate-300 rounded-full"></div>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-slate-300 rounded-full"></div>
                      <div className="h-3 w-24 bg-slate-300 rounded"></div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-slate-300 rounded-full"></div>
                      <div className="h-3 w-32 bg-slate-300 rounded"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Team Mood Card */}
              <div className="bg-white rounded-lg p-6 border border-slate-200 shadow-sm">
                <div className="flex items-center space-x-2 mb-4">
                  <div className="h-4 w-24 bg-slate-300 rounded"></div>
                  <div className="h-6 w-6 bg-slate-300 rounded"></div>
                </div>
                <div className="text-center">
                  <div className="mb-4">
                    <div className="w-16 h-16 bg-slate-300 rounded-full mx-auto mb-2"></div>
                    <div className="h-6 w-20 bg-slate-300 rounded mx-auto mb-1"></div>
                    <div className="h-3 w-16 bg-slate-200 rounded mx-auto"></div>
                  </div>
                  <div className="h-4 w-32 bg-slate-300 rounded mx-auto"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Daily Summary Section Skeleton */}
          <div className="mb-8">
            <div className="bg-white rounded-lg p-6 border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <div className="h-6 w-6 bg-slate-300 rounded"></div>
                  <div className="h-6 w-40 bg-slate-300 rounded"></div>
                  <div className="h-6 w-20 bg-slate-300 rounded-full"></div>
                </div>
                <div className="h-4 w-32 bg-slate-200 rounded"></div>
              </div>
              
              <div className="space-y-6">
                {/* Overview */}
                <div className="space-y-2">
                  <div className="h-4 w-full bg-slate-300 rounded"></div>
                  <div className="h-4 w-3/4 bg-slate-300 rounded"></div>
                  <div className="h-4 w-1/2 bg-slate-300 rounded"></div>
                </div>
                
                {/* Three columns */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[1, 2, 3].map((col) => (
                    <div key={col}>
                      <div className="flex items-center space-x-2 mb-3">
                        <div className="h-4 w-4 bg-slate-300 rounded"></div>
                        <div className="h-4 w-32 bg-slate-300 rounded"></div>
                      </div>
                      <div className="space-y-2">
                        {[1, 2, 3].map((item) => (
                          <div key={item} className="flex items-start space-x-2">
                            <div className="h-2 w-2 bg-slate-300 rounded-full mt-1"></div>
                            <div className="h-4 w-full bg-slate-200 rounded"></div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Three Column Layout Skeleton */}
          <div className="grid grid-cols-3 gap-8">
            {['Previous work day progress', 'Plans for today', 'Any blockers?'].map((title, index) => (
              <div key={index}>
                <div className="h-6 w-48 bg-slate-300 rounded mb-4"></div>
                <div className="space-y-3">
                  {[1, 2, 3].map((item) => (
                    <div key={item} className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-slate-300 rounded-full"></div>
                        <div className="flex-1 min-w-0 space-y-2">
                          <div className="h-4 w-24 bg-slate-300 rounded"></div>
                          <div className="h-3 w-16 bg-slate-200 rounded"></div>
                          <div className="space-y-1">
                            <div className="h-4 w-full bg-slate-200 rounded"></div>
                            <div className="h-4 w-3/4 bg-slate-200 rounded"></div>
                          </div>
                          <div className="h-3 w-20 bg-slate-200 rounded"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SkeletonLoader; 